<div class="container">

  <!-- Header -->
  <div class="header">
    <h1>Database Changes Manager</h1>
    <div class="user-info" *ngIf="currentUser">
      <span class="user-icon">👤</span>
      <span class="username">{{ currentUser }}</span>
      <span class="auth-badge" [class.authenticated]="isAuthenticated">
        {{ isAuthenticated ? '✓ Authenticated' : '⚠ Not Authenticated' }}
      </span>
    </div>
  </div>

  <!-- Error Message -->
  <div class="error" *ngIf="error">
    {{ error }}
    <button (click)="error = ''" class="close-error">×</button>
  </div>

  <!-- Loading Indicator -->
  <div class="loading" *ngIf="loading">Loading...</div>

  <!-- ── Step 1: Select Database ──────────────────────────────────── -->
  <div class="step-card">
    <div class="step-header">
      <span class="step-number">1</span>
      <span class="step-title">Select Database</span>
    </div>
    <div class="step-body">
      <select [(ngModel)]="selectedDatabase" (change)="onDatabaseChange()" class="select-input">
        <option value="">— Choose a database —</option>
        <option *ngFor="let db of databases" [value]="db">{{ db }}</option>
      </select>
    </div>
  </div>

  <!-- ── Step 2: Select Version + Export ─────────────────────────── -->
  <div class="step-card" *ngIf="selectedDatabase">
    <div class="step-header">
      <span class="step-number">2</span>
      <span class="step-title">Version</span>
      <button (click)="openNewVersionForm()" class="btn btn-sm btn-primary step-action-btn">
        + New Version
      </button>
    </div>

    <div class="step-body" *ngIf="versions.length > 0; else noVersions">
      <div class="version-row">

        <!-- Version selector + module chips -->
        <div class="version-select-group ">
          <div class="select-wrap">
            <label class="export-label">Select version:</label>
            <select [(ngModel)]="selectedVersion" (change)="onVersionChange()" class="select-input">
              <option *ngFor="let v of versions" [ngValue]="v">
                {{ v.versionName === 'Unreleased' ? 'Current (unreleased)' : (v.versionName || 'v?') + '  —  ' + (v.versionDate | date:'mediumDate') }}
              </option>
            </select>
          </div>
          <div class="version-modules" *ngIf="selectedVersion">
            <span class="modules-label">Roles:</span>
            <ng-container *ngIf="selectedVersion!.affectedRoles && selectedVersion!.affectedRoles!.length > 0; else noModules">
              <span class="module-chip" *ngFor="let role of selectedVersion!.affectedRoles">
                {{ role.roleTypeName }}
              </span>
            </ng-container>
            <ng-template #noModules>
              <span class="modules-none">None</span>
            </ng-template>
          </div>
        </div>

        <!-- Export controls (client filter + export button) -->
        <div class="export-group">
          <div class="select-wrap">
            <label class="export-label">Export for:</label>
            <select [(ngModel)]="selectedClient" (change)="onClientChange()" class="select-input client-select">
              <option [ngValue]="null">All (no client filter)</option>
              <option *ngFor="let client of clients" [ngValue]="client">
                {{ client.clientName }}
              </option>
            </select>
          </div>
          <button (click)="exportVersion()" [disabled]="!selectedVersion" class="btn btn-success export-btn">
            ↓ Export{{ selectedClient ? ' for ' + selectedClient.clientName : ' All' }}
          </button>
        </div>

      </div>
    </div>

    <ng-template #noVersions>
      <div class="step-body">
        <p class="no-data">No versions found for this database.</p>
      </div>
    </ng-template>
  </div>

  <!-- ── Step 3: Database Logs ────────────────────────────────────── -->
  <div class="step-card logs-card" *ngIf="selectedVersion">
    <div class="step-header">
      <span class="step-number">3</span>
      <span class="step-title">Database Logs</span>
      <div class="step-header-right">
        <span class="record-count" *ngIf="totalCount > 0">{{ totalCount }} record{{ totalCount !== 1 ? 's' : '' }}</span>
        <button (click)="openNewLogForm()" class="btn btn-sm btn-primary step-action-btn">+ Add SQL</button>
      </div>
    </div>

    <div class="step-body">

      <!-- Filters -->
      <div class="filters-container">

        <div class="filters-row">
          <div class="filter-group">
            <label>Object Type</label>
            <select [(ngModel)]="objectTypeFilter" (change)="currentPage = 1; loadLogs()" class="select-input">
              <option value="">All</option>
              <option *ngFor="let type of getUniqueObjectTypes()" [value]="type">{{ type }}</option>
            </select>
          </div>
          <div class="filter-group">
            <label>User</label>
            <select [(ngModel)]="userNameFilter" (change)="currentPage = 1; loadLogs()" class="select-input">
              <option value="">All</option>
              <option *ngFor="let user of getUniqueUserNames()" [value]="user">{{ user }}</option>
            </select>
          </div>
          <div class="filter-group filter-group--grow">
            <label>Object Name</label>
            <input type="text" [(ngModel)]="objectNameFilter"
                   (keyup.enter)="currentPage = 1; loadLogs()"
                   placeholder="Search…"
                   class="input" />
          </div>
          <div class="filter-group filter-group--narrow">
            <label>Rows / page</label>
            <select [(ngModel)]="pageSize" (change)="onPageSizeChange()" class="select-input">
              <option *ngFor="let size of pageSizeOptions" [value]="size">{{ size }}</option>
            </select>
          </div>
        </div>

        <div class="filters-row">
          <div class="filter-group filter-group--date">
            <label>Date From</label>
            <input type="datetime-local" [(ngModel)]="dateFromFilter"
                   (change)="currentPage = 1; loadLogs()"
                   class="input" />
          </div>
          <div class="filter-group filter-group--date">
            <label>Date To</label>
            <input type="datetime-local" [(ngModel)]="dateToFilter"
                   (change)="currentPage = 1; loadLogs()"
                   class="input" />
          </div>
          <div class="filter-group filter-group--labels">
            <label>Labels</label>
            <div class="label-autocomplete">
              <div class="selected-labels">
                <span class="label-chip" *ngFor="let label of selectedLabelFilters">
                  {{ label.labelDesc }}
                  <button type="button" class="remove-label" (click)="removeLabelFilter(label)">×</button>
                </span>
                <input type="text"
                       [(ngModel)]="labelSearchText"
                       (input)="onLabelSearchChange()"
                       (focus)="onLabelSearchChange()"
                       (blur)="hideLabelSuggestions()"
                       placeholder="Search labels…"
                       class="label-search-input" />
              </div>
              <div class="label-suggestions" *ngIf="showLabelSuggestions">
                <div class="suggestion-item"
                     *ngFor="let label of filteredLabelOptions"
                     (mousedown)="selectLabelFilter(label)">
                  {{ label.labelDesc }}
                </div>
              </div>
            </div>
          </div>
          <div class="filter-group filter-group--narrow" *ngIf="selectedLabelFilters.length > 1">
            <label>Match</label>
            <select [(ngModel)]="labelMatchMode" (change)="onLabelMatchModeChange()" class="select-input">
              <option value="any">Any label</option>
              <option value="all">All labels</option>
            </select>
          </div>
          <div class="filter-group filter-group--action">
            <label>&nbsp;</label>
            <button (click)="clearAllFilters()" class="btn btn-secondary btn-sm">Clear filters</button>
          </div>
        </div>

      </div><!-- /filters-container -->

      <!-- Pagination info -->
      <div class="pagination-info" *ngIf="totalCount > 0">
        Showing {{ (currentPage - 1) * pageSize + 1 }}–{{ Math.min(currentPage * pageSize, totalCount) }} of {{ totalCount }}
      </div>

      <!-- Logs Table -->
      <table class="logs-table" *ngIf="filteredLogs.length > 0">
        <thead>
          <tr>
            <th>ID</th>
            <th>Date / Time</th>
            <th>User</th>
            <th>Schema</th>
            <th>Object Name</th>
            <th>Type</th>
            <th>Event</th>
            <th>Labels</th>
            <th>Command Preview</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let log of filteredLogs">
            <td class="col-id">{{ log.id }}</td>
            <td class="col-date">{{ log.dateTime | date:'short' }}</td>
            <td>{{ log.userName }}</td>
            <td>{{ log.schemaName }}</td>
            <td>{{ log.objectName }}</td>
            <td>{{ log.objectType }}</td>
            <td>{{ log.eventType }}</td>
            <td class="labels-cell">
              <div class="labels-container">
                <span class="label-badge" *ngFor="let label of log.labels">{{ label }}</span>
                <button (click)="openLabelModal(log)" class="btn btn-link btn-sm" title="Manage Labels">🏷️</button>
              </div>
            </td>
            <td class="command-preview">
              <div class="command-text" [title]="log.commandText || ''">
                {{ (log.commandText || '').substring(0, 100) }}{{ (log.commandText || '').length > 100 ? '…' : '' }}
              </div>
            </td>
            <td>
              <button (click)="deleteLog(log)" class="btn btn-danger btn-sm">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>

      <p class="no-data" *ngIf="filteredLogs.length === 0 && !loading">No logs found for this version.</p>

      <!-- Pagination Controls -->
      <div class="pagination-controls" *ngIf="totalPages > 1">
        <button class="btn btn-sm btn-secondary" (click)="goToPage(1)" [disabled]="currentPage === 1" title="First">««</button>
        <button class="btn btn-sm btn-secondary" (click)="previousPage()" [disabled]="currentPage === 1" title="Previous">«</button>
        <span class="page-numbers">
          <button *ngFor="let page of getPageNumbers()"
                  class="btn btn-sm"
                  [class.btn-primary]="page === currentPage"
                  [class.btn-secondary]="page !== currentPage"
                  (click)="goToPage(page)">{{ page }}</button>
        </span>
        <button class="btn btn-sm btn-secondary" (click)="nextPage()" [disabled]="currentPage === totalPages" title="Next">»</button>
        <button class="btn btn-sm btn-secondary" (click)="goToPage(totalPages)" [disabled]="currentPage === totalPages" title="Last">»»</button>
        <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
      </div>

    </div><!-- /step-body -->
  </div><!-- /step 3 -->


  <!-- ── New Version Modal ─────────────────────────────────────────── -->
  <div class="modal" *ngIf="showNewVersionForm" (click)="cancelNewVersion()">
    <div class="modal-content" (click)="$event.stopPropagation()">
      <button class="modal-close" (click)="cancelNewVersion()">✖</button>
      <h3>Create New Version</h3>
      <p class="form-hint" *ngIf="previousVersionName">
        Previous version: <strong>{{ previousVersionName }}</strong>
      </p>
      <div class="form-group">
        <label>Version Name</label>
        <input type="text" [(ngModel)]="newVersionName" class="input" placeholder="e.g., v1.0.0" />
      </div>
      <div class="form-group">
        <label>Affected Modules</label>
        <p class="form-hint">Select which modules are affected by this version.</p>
        <div class="roles-checklist" *ngIf="roleTypes.length > 0; else noRoles">
          <label class="role-checkbox" *ngFor="let role of roleTypes">
            <input type="checkbox"
                   [checked]="isRoleTypeSelected(role.roleTypeID)"
                   (change)="toggleRoleType(role.roleTypeID)" />
            <span class="role-name">{{ role.roleTypeName }}</span>
            <span class="role-desc" *ngIf="role.description">{{ role.description }}</span>
            <span class="artifact-badge" *ngIf="artifactStatus(role.roleTypeID)">
              Built {{ artifactStatus(role.roleTypeID)?.lastBuiltDate | date:'short' }}
            </span>
            <span class="no-artifact" *ngIf="!artifactStatus(role.roleTypeID)">No artifact</span>
          </label>
        </div>
        <ng-template #noRoles>
          <p class="no-roles-hint">No modules configured. Add them in Role Types Management.</p>
        </ng-template>
        <div class="selected-summary" *ngIf="selectedRoleTypeIds.length > 0">
          {{ selectedRoleTypeIds.length }} module(s) selected
        </div>
      </div>
      <div class="modal-actions">
        <button (click)="cancelNewVersion()" class="btn btn-secondary">Cancel</button>
        <button (click)="saveNewVersion()" class="btn btn-primary">Save</button>
      </div>
    </div>
  </div>

  <!-- ── New Log Modal ─────────────────────────────────────────────── -->
  <div class="modal" *ngIf="showNewLogForm" (click)="cancelNewLog()">
    <div class="modal-content modal-content--large" (click)="$event.stopPropagation()">
      <button class="modal-close" (click)="cancelNewLog()">✖</button>
      <h3>Add New SQL Script</h3>
      <div class="form-group">
        <label>Schema Name</label>
        <input type="text" [(ngModel)]="newLog.schemaName" class="input" />
      </div>
      <div class="form-group">
        <label>Object Name</label>
        <input type="text" [(ngModel)]="newLog.objectName" class="input" />
      </div>
      <div class="form-group">
        <label>Object Type</label>
        <select [(ngModel)]="newLog.objectType" class="select-input">
          <option value="TABLE">TABLE</option>
          <option value="VIEW">VIEW</option>
          <option value="PROCEDURE">PROCEDURE</option>
          <option value="FUNCTION">FUNCTION</option>
          <option value="TRIGGER">TRIGGER</option>
          <option value="OTHER">OTHER</option>
        </select>
      </div>
      <div class="form-group">
        <label>Event Type</label>
        <input type="text" [(ngModel)]="newLog.eventType" class="input" />
      </div>
      <div class="form-group">
        <label>SQL Command</label>
        <textarea [(ngModel)]="newLog.commandText" class="textarea" rows="10"></textarea>
      </div>
      <div class="modal-actions">
        <button (click)="cancelNewLog()" class="btn btn-secondary">Cancel</button>
        <button (click)="saveNewLog()" class="btn btn-primary">Save</button>
      </div>
    </div>
  </div>

  <!-- ── Label Modal ───────────────────────────────────────────────── -->
  <div class="modal" *ngIf="showLabelModal" (click)="closeLabelModal()">
    <div class="modal-content" (click)="$event.stopPropagation()">
      <button class="modal-close" (click)="closeLabelModal()">✖</button>
      <h3>Labels — Log #{{ selectedLogForLabels?.id }}</h3>
      <div class="label-selection">
        <div class="label-item" *ngFor="let label of availableLabels">
          <label class="checkbox-label">
            <input type="checkbox" [checked]="hasLabel(label)" (change)="toggleLabel(label)" />
            <span class="label-text">{{ label.labelDesc }}</span>
          </label>
        </div>
      </div>
      <div class="modal-actions">
        <button (click)="closeLabelModal()" class="btn btn-primary">Done</button>
      </div>
    </div>
  </div>

</div>

<a #exportAnchor style="display:none"></a>
